// ---------------------------------------------------------------------------
// Auto-save bridge — connects EditorStore + FlowEditorStore to auto-save
//
// Subscribes to editor mutations and queues saves to Supabase via auto-save.ts.
// Also syncs flow editor changes back to project.flows for preview.
// ---------------------------------------------------------------------------

import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import { resolveFlowOwner, type OwnedBlock } from '@/features/flow-editor/services/flow-ownership'
import {
  queueSave,
  flushSaves,
  hasPendingChanges,
  discardPendingSavesForProject,
  registerAutoSaveProjectIdSource,
  beginReloadFence,
  waitForActiveFlush,
  hasCoordinatedPersistenceInFlight,
} from './auto-save'
import { serializeDesignSystem, toDesignSystemJson } from '@/features/design-system/lib/design-system-export'
import { computeThemeTokens } from '@/features/design-system/lib/theme-tokens'
import {
  persistGeneratedBlocks,
  saveProjectMeta,
  withServerDatasourceReadinessManifestAuthority,
  type StudioProjectServerRevisionRef,
} from '@/services/project-api'
import { withStudioProjectPlanSettings } from '@/features/project/lib/studio-project-plan'
import {
  buildUiFieldPayload,
  getPersistableDatasource,
  getPersistableVariables,
  shouldSkipEditorPersistSnapshot,
  shouldSkipPersistSnapshot,
} from './auto-save-payload'
import { mergeFlowStepsForSave } from './auto-save-flow'
import { validateFlowEditAndPublish } from './auto-save-flow-validation'
import { toast } from '@/store/toast-store'
// Uses 'any' for immer setState to avoid complex LoadedApp type casting
export {
  getPersistableDatasource,
  getPersistableVariables,
  shouldSkipEditorPersistSnapshot,
  shouldSkipPersistSnapshot,
} from './auto-save-payload'

let initialized = false
let lastBlocksSnapshot = ''
let lastEditorProjectId = ''
let editorFullPersistTimer: ReturnType<typeof setTimeout> | null = null
type FullEditorPersistInput = {
  projectId: string
  app: unknown
  blocks: Record<string, unknown>
  flows: Record<string, unknown>
}
let pendingFullEditorPersist: FullEditorPersistInput | null = null
let activeFullEditorPersist: Promise<void> | null = null
let editorAutoSaveSuppressionDepth = 0

/**
 * Mirrors an already-persisted coordinated mutation into the editor without
 * scheduling a duplicate block PATCH. The subscription still re-baselines
 * its snapshot so the next user edit is detected normally.
 */
export function withEditorAutoSaveSuppressed<T>(mutate: () => T): T {
  editorAutoSaveSuppressionDepth += 1
  try {
    return mutate()
  } finally {
    editorAutoSaveSuppressionDepth -= 1
  }
}

// ---------------------------------------------------------------------------
// Dedupe-snapshot reset registry (PI-L2-05 / RUX-1)
//
// Every persistence lane keeps a closure-scoped "last serialization" cache (and
// some keep a per-project marker) so a re-emission with unchanged content is
// not re-queued. A conflict reload re-seeds the project from the server — so
// the editor/flow/variables/datasource/design-system caches all hold PRE-reload
// serializations. Two failure modes result:
//   - the reload's OWN synchronous re-emissions (openServerProject's set() +
//     loadApp) would be treated as edits and spuriously re-queue the
//     just-loaded content — so the caches must be reset BEFORE the reload;
//   - a byte-identical re-apply of the conflicted edit AFTER the reload would
//     match its stale cache and be dedupe-suppressed (never queued, never
//     dirty → silent loss on tab close).
//
// The caches live inside initAutoSaveBridge's closure (and the editor lane at
// module scope), unreachable from the module-level reload function. Each lane
// registers a capture-and-reset callback here: it resets its cache to the
// "fresh project" state AND returns a restorer. resolveSaveConflictByReload
// resets every lane BEFORE the reload, then on FAILURE restores them all (the
// project never reloaded, so the pre-reload change-detection state must stand,
// or the user's next edit would hit a spurious project-changed re-baseline and
// be swallowed — the L2-COR-03 "bonus" silent-swallow).
type DedupeReset = () => () => void
const dedupeSnapshotResets = new Set<DedupeReset>()

/** Register a lane's capture-and-reset. Called once per lane at bridge init;
 * the returned disposer is unused in production (the bridge is a singleton)
 * but lets the vitest suite avoid leaking stale resets across re-inits. */
function registerDedupeSnapshotReset(reset: DedupeReset): () => void {
  dedupeSnapshotResets.add(reset)
  return () => dedupeSnapshotResets.delete(reset)
}

/** Reset every registered lane's dedupe cache, returning a restorer that puts
 * them all back (used only when the reload then fails). After the reset, the
 * next emission of each lane re-baselines from the (reloaded) state instead of
 * being suppressed as a no-op or queued as a spurious edit. */
function resetAllDedupeSnapshots(): () => void {
  // Module-level editor lane: clear both the snapshot AND the project marker so
  // the editor subscription's editorProjectChanged branch re-baselines the
  // reloaded loadApp emission (instead of treating it as a fresh edit).
  const prevEditorProjectId = lastEditorProjectId
  const prevBlocksSnapshot = lastBlocksSnapshot
  lastEditorProjectId = ''
  lastBlocksSnapshot = ''

  const restorers: Array<() => void> = []
  for (const reset of dedupeSnapshotResets) restorers.push(reset())

  return () => {
    lastEditorProjectId = prevEditorProjectId
    lastBlocksSnapshot = prevBlocksSnapshot
    for (const restore of restorers) restore()
  }
}

// Store reference injected via initAutoSaveBridge() to avoid circular
// dependency with studio-store.ts (which dynamically imports this module).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let _studioStore: any = null

function getStudioStore() {
  if (!_studioStore) {
    throw new Error('[auto-save-bridge] Not initialized — call initAutoSaveBridge(store) first')
  }
  return _studioStore
}

// ---------------------------------------------------------------------------
// Field-scoped editor → studio block mirror (PI-1/PI-6)
//
// OWNERSHIP RULE: the page-editor store owns exactly the block fields its
// slices mutate after loadApp — the `ui` element tree (INCLUDING element-level
// events nested in ui.root), `meta`, and block creation/deletion. It NEVER
// writes the block-level `flow`, `events`, `state`/`variables`, or
// `data`/`datasource` fields after load (loadApp seeds them once and nothing
// in page-editor updates them) — those are written exclusively into
// currentProject by the studio side: the F3 flow rename/delete/restore tools,
// the flowSteps subscription below, and data-slice's variables / datasources
// / addEventBinding. A wholesale `currentProject.blocks = editorBlocks`
// mirror therefore reverted every studio-side rewrite to the load-time copy —
// and when the revert landed BETWEEN a flow rewrite and its
// queueFlowBlockFieldSave (the rename handler's own editor mirror re-enters
// this subscription synchronously), the STALE pre-rename flow JSON is what
// got PATCHed. Merge per field instead: editor copy for editor-owned fields,
// existing studio copy for studio-owned ones. Editor-created blocks (no
// studio counterpart) flow through whole; blocks deleted editor-side
// disappear (the merge iterates editor keys only) — exactly the previous
// add/delete semantics.
const STUDIO_OWNED_BLOCK_FIELDS = [
  'flow',
  'events',
  'state',
  'variables',
  'data',
  'datasource',
] as const

export function mergeEditorBlocksPreservingStudioFields(
  editorBlocks: Record<string, unknown>,
  studioBlocks: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const merged: Record<string, unknown> = {}
  for (const [blockName, editorBlock] of Object.entries(editorBlocks)) {
    const studioBlock = studioBlocks?.[blockName] as Record<string, unknown> | undefined
    if (!studioBlock || !editorBlock || typeof editorBlock !== 'object') {
      merged[blockName] = editorBlock
      continue
    }
    const next: Record<string, unknown> = { ...(editorBlock as Record<string, unknown>) }
    for (const field of STUDIO_OWNED_BLOCK_FIELDS) {
      if (field in studioBlock) next[field] = studioBlock[field]
    }
    merged[blockName] = next
  }
  return merged
}

/** The single editor→studio in-memory mirror: field-scoped (see ownership
 *  rule above), preserving the preview re-render purpose of the wholesale
 *  mirror it replaces. */
function mirrorEditorBlocksIntoProject(blocks: Record<string, unknown>, app: unknown): void {
  const storeApi = getStudioStore()
  const merged = mergeEditorBlocksPreservingStudioFields(
    blocks,
    storeApi.getState().currentProject?.blocks as Record<string, unknown> | undefined,
  )
  storeApi.setState((s: any) => {
    if (s.currentProject) {
      s.currentProject.blocks = merged
      s.currentProject.manifest = app
    }
  })
}

function projectRevisionCounter(
  projectId: string,
  revision: StudioProjectServerRevisionRef,
): number | null {
  if (revision.authority !== 'studio-project-server') return null
  const prefix = `${projectId}@`
  if (!revision.value.startsWith(prefix)) return null
  const counter = Number(revision.value.slice(prefix.length))
  return Number.isSafeInteger(counter) && counter >= 1 ? counter : null
}

async function persistFullEditorSnapshot(
  input: FullEditorPersistInput,
  opts?: Pick<RequestInit, 'keepalive'>,
) {
  const storeApi = getStudioStore()
  const store = storeApi.getState()
  // A captured project-A snapshot must not read project B's live blocks or
  // readiness authority after a fast project switch. Project switching owns
  // the normal pre-switch flush; a residual delayed timer is stale and aborts.
  if (store.currentProjectId !== input.projectId) return
  // Server payload: merge against the LIVE studio blocks at flush time — the
  // debounce window may contain newer studio-side flow/events/variables
  // writes that the editor snapshot must not clobber on the server either.
  const blocks = mergeEditorBlocksPreservingStudioFields(
    input.blocks,
    store.currentProject?.blocks as Record<string, unknown> | undefined,
  )
  const manifest = withServerDatasourceReadinessManifestAuthority(
    input.app,
    store.projectMeta,
  )
  try {
    storeApi.setState({ isSaving: true } as never)
    const parentRevision = store.projectMeta?.id === input.projectId
      ? store.projectMeta.projectRevision
      : undefined
    const parentCounter = parentRevision
      ? projectRevisionCounter(input.projectId, parentRevision)
      : null
    if (!parentRevision || parentCounter === null) {
      throw new Error('Exact server project revision is unavailable for IDE save')
    }
    // L2-COR-05/CS-4/RUX-3 — thread keepalive into BOTH lane-2 writes so a
    // dismissal-time flush can outlive a direct tab close. persistGeneratedBlocks
    // is the PUT whole-replace; saveProjectMeta is the manifest PATCH (it
    // already accepts the keepalive opt — F27). Without this, the document
    // teardown aborts these fetches and a just-added block is lost even though
    // the user was prompted and consented to leave.
    const persisted = await persistGeneratedBlocks(
      input.projectId,
      blocks,
      input.flows,
      {
        ...(opts?.keepalive ? { keepalive: true } : {}),
        expectedProjectRevision: parentRevision,
      },
    )
    const successorCounter = projectRevisionCounter(
      input.projectId,
      persisted.projectRevision,
    )
    if (successorCounter !== parentCounter + 1) {
      throw new Error('IDE save did not receive the exact successor project revision')
    }
    // The block write has already committed even if the following manifest
    // PATCH fails. Advance the store immediately so a retry never replays the
    // stale parent revision over the successful authority-issued successor.
    storeApi.setState((s: any) => {
      if (s.currentProjectId !== input.projectId || s.projectMeta?.id !== input.projectId) return
      s.projectMeta.artifact_revision = successorCounter
      s.projectMeta.projectRevision = persisted.projectRevision
    })
    await saveProjectMeta(input.projectId, { manifest }, opts)
    storeApi.setState((s: any) => {
      s.blockIdMap = persisted.blockIdMap
      if (s.currentProject) {
        // Re-merge against the draft: studio-side writes can land during the
        // awaits above, and the in-memory mirror must not revert them.
        s.currentProject.blocks = mergeEditorBlocksPreservingStudioFields(
          input.blocks,
          s.currentProject.blocks as Record<string, unknown> | undefined,
        )
        s.currentProject.manifest = manifest
      }
    })
    store.markSaved()
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Auto-save failed'
    store.markSaveFailed(`Auto-save failed: ${message}`)
  }
}

function scheduleFullEditorPersist(input: FullEditorPersistInput) {
  pendingFullEditorPersist = input
  if (editorFullPersistTimer) clearTimeout(editorFullPersistTimer)
  editorFullPersistTimer = setTimeout(() => {
    editorFullPersistTimer = null
    void flushEditorFullPersist()
  }, 500)
}

function getCurrentEditorPersistInput(): FullEditorPersistInput | null {
  const store = getStudioStore().getState()
  if (store.projectSource !== 'server' || !store.currentProjectId) return null

  const { app, blocks, flows } = useEditorStore.getState()
  if (!app || !blocks || Object.keys(blocks).length === 0) return null

  const snapshot = buildEditorSnapshot(app, blocks as Record<string, unknown>)
  const hasUnpersistedBlock = Object.keys(blocks).some((blockName) => !store.blockIdMap[blockName])
  if (!hasUnpersistedBlock && snapshot === lastBlocksSnapshot) return null

  lastBlocksSnapshot = snapshot
  return {
    projectId: store.currentProjectId,
    app,
    blocks: blocks as Record<string, unknown>,
    flows: flows as Record<string, unknown>,
  }
}

/** Lane-2 pending-state visibility (F27): true while the full editor
 * persist is debounce-armed, captured-but-unflushed, or in flight. The
 * dismissal guard consults this alongside hasPendingChanges() because the
 * full-persist lane never touches auto-save.ts's pendingChanges map. */
export function hasPendingEditorPersist(): boolean {
  return (
    editorFullPersistTimer !== null ||
    pendingFullEditorPersist !== null ||
    activeFullEditorPersist !== null
  )
}

/**
 * F27 — composable dismissal-guard predicate: is there editor work that has
 * not durably landed on the server? Covers BOTH debounced persistence lanes
 * plus any in-flight persist:
 *  - Lane 1 (queueSave field PATCHes, incl. the #991 flow/events/ui
 *    rewrites): hasPendingChanges() — debounce-queued, failed-re-queued,
 *    and in-flight.
 *  - Lane 2 (page-editor full snapshot): hasPendingEditorPersist().
 *  - Direct coordinated writes: hasCoordinatedPersistenceInFlight().
 *  - isSaving: the shared store flag both lanes' flush sections set, so a
 *    slow full persist holds the guard until it settles (intended).
 *
 * Kept as a single composable function on purpose. F11's
 * optimistic-concurrency conflict halt clears pendingChanges WITHOUT the
 * edit landing — saveConflict is OR-ed in here so the guard does not waive
 * exactly the divergence F11 blocked from retrying: a conflict-halted edit
 * still blocks dismissal until the user resolves (reload) or abandons it.
 */
export function shouldBlockDismissal(): boolean {
  return (
    hasPendingChanges() ||
    hasPendingEditorPersist() ||
    hasCoordinatedPersistenceInFlight() ||
    _studioStore?.getState().isSaving === true ||
    _studioStore?.getState().saveConflict != null
  )
}

export async function flushEditorFullPersist(
  opts?: Pick<RequestInit, 'keepalive'>,
): Promise<void> {
  if (editorFullPersistTimer) {
    clearTimeout(editorFullPersistTimer)
    editorFullPersistTimer = null
  }

  if (activeFullEditorPersist) {
    await activeFullEditorPersist
  }

  // Opportunistic capture — INTENTIONALLY reserved for explicit user-intent
  // flush points (publish-modal / publishCurrentProject): it synthesizes a
  // persist input from the CURRENT editor state even when nothing was ever
  // scheduled, which is what guarantees publish ships exactly what the user
  // sees (including chat-generated blocks that have no DB rows yet, whose
  // subscription emissions never queued lane-1 saves). Automatic, no-intent
  // triggers (the visibilitychange dismissal flush) must gate on
  // hasPendingEditorPersist() BEFORE calling this function: in the DEFAULT
  // preview-rail session the editor subscription's surface gate returns
  // before the project re-baseline, so lastBlocksSnapshot never matches the
  // loaded content and this capture would treat pristine LOAD-TIME state as
  // new work — synthesizing an unrequested whole-replace PUT (no If-Match on
  // the PUT path) that silently stomps other sessions' edits on a mere tab
  // switch (L2-COR-01 P0).
  if (!pendingFullEditorPersist) {
    pendingFullEditorPersist = getCurrentEditorPersistInput()
  }

  while (pendingFullEditorPersist) {
    const input = pendingFullEditorPersist
    pendingFullEditorPersist = null
    activeFullEditorPersist = persistFullEditorSnapshot(input, opts).finally(() => {
      activeFullEditorPersist = null
    })
    await activeFullEditorPersist
  }
}

/**
 * F11 — CANCEL (not flush) the full editor persist lane: disarm the
 * debounce timer, drop the captured-but-unflushed snapshot, and await any
 * already-in-flight persist so it cannot land after the caller proceeds.
 * Used by the conflict-resolution reload: a snapshot captured PRE-conflict
 * would PUT-replace the freshly reloaded server state wholesale — the exact
 * stomp the reload exists to escape.
 */
export async function cancelPendingEditorFullPersist(): Promise<void> {
  // CS-2 — loop until the lane is quiescent. A single pass left a hole: an
  // editor subscription re-emission during the `await activeFullEditorPersist`
  // below can re-arm the debounce timer (scheduleFullEditorPersist) or, worse,
  // an in-flight persist's `.finally` can start the NEXT queued snapshot — so
  // after the await we must re-clear and re-await until nothing is armed,
  // captured, or in flight. Otherwise a stale pre-reload snapshot's 500ms
  // timer (or its chained successor) fires the wholesale PUT-replace AFTER the
  // reload, the exact stomp this cancel exists to prevent.
  for (;;) {
    if (editorFullPersistTimer) {
      clearTimeout(editorFullPersistTimer)
      editorFullPersistTimer = null
    }
    pendingFullEditorPersist = null
    if (!activeFullEditorPersist) {
      // Nothing in flight. If nothing re-armed synchronously above, we're done.
      if (!editorFullPersistTimer && !pendingFullEditorPersist) return
      continue
    }
    try {
      await activeFullEditorPersist
    } catch {
      // Settle only — the in-flight persist's own catch handles reporting.
    }
    // Re-check: the awaited persist's .finally may have started a successor,
    // or a subscription emission may have re-armed the timer during the await.
  }
}

/** CS-10 — single-flight latch: a second Reload click while the first
 * resolve is parked on an await (confirm / cancel-persist / reload) would
 * run two concurrent openServerProject loads. The latch early-returns the
 * reentrant call; the banner also disables its button off isResolvingConflict
 * (set/cleared below), so the double-click is closed on both ends. */
let resolveConflictInProgress = false

/**
 * F11 — resolve an unresolved save conflict by reloading the project from
 * the server (the canonical in-app reload: openServerProject re-runs
 * loadProject, which re-seeds blockIdMap AND the OCC baselines).
 *
 * Safety order (hardened — L2-COR-03/04, CS-2/3, PI-L2-04/05, RUX-1):
 *  1. Reentrancy latch (CS-10): one resolve at a time.
 *  2. If OTHER unsaved work exists (lane-1 queue / lane-2 snapshot), ask
 *     the user first — the reload discards it, and silent discard is the
 *     failure class this whole lane exists to remove.
 *  3. Cancel the pending full editor persist (cancelPendingEditorFullPersist
 *     — loops until quiescent so no pre-reload snapshot PUT-stomps the reload).
 *  4. Fence in-flight lane-1 work (PI-L2-04/CS-3): drop the queue, await the
 *     active flush chain, then bump the reload epoch so any straggler that
 *     outran the await drops its settle-time re-queue + conflict-arm.
 *  5. Reload, and ONLY on demonstrated success (currentProjectId advanced to
 *     the target with no projectLoadError) clear the conflict and re-baseline
 *     EVERY lane's dedupe cache. On failure: keep the conflict armed (the
 *     dismissal guard stays up over the still-diverged, never-reloaded state)
 *     and surface a humanized toast — the L2-COR-03 silent-discard hole.
 *
 * Returns true when the reload demonstrably succeeded, false when the user
 * declined, there was nothing to resolve, a resolve is already running, or
 * the reload failed.
 */
export async function resolveSaveConflictByReload(): Promise<boolean> {
  if (resolveConflictInProgress) return false

  const storeApi = getStudioStore()
  const conflict = storeApi.getState().saveConflict as { projectId?: string } | null
  if (!conflict) return false
  const projectId = conflict.projectId || storeApi.getState().currentProjectId
  if (!projectId) {
    storeApi.getState().clearSaveConflict()
    return false
  }

  if (hasPendingChanges() || hasPendingEditorPersist()) {
    const proceed = typeof window !== 'undefined' && typeof window.confirm === 'function'
      ? window.confirm(
          'You have other unsaved changes. Reloading will replace them with the latest from the server — reload anyway?',
        )
      : true
    if (!proceed) return false
  }

  resolveConflictInProgress = true
  storeApi.setState({ isResolvingConflict: true } as never)
  try {
    // Lane 2: cancel the captured/armed/in-flight full editor persist.
    await cancelPendingEditorFullPersist()

    // Lane 1 fence (PI-L2-04 / CS-3): first serialize against the CURRENT
    // in-flight dispatch chain so any leg already on the wire SETTLES before
    // the reload reads /full — a transient-failed leg re-queues its stale
    // payload at settle time, so the discard must run AFTER the await to sweep
    // it. (Discarding only before the await would miss the re-queue.) A flush
    // can chain on during the await, so loop until the chain is quiescent.
    let prevFlush: Promise<void> | null = null
    for (let i = 0; i < 5; i += 1) {
      const current = waitForActiveFlush()
      if (current === prevFlush) break
      prevFlush = current
      await current
    }
    discardPendingSavesForProject(projectId)
    // Epoch fence: anything that still outran the awaits above (a straggler
    // settling after this point) drops its re-queue + conflict-arm. Bumped
    // AFTER the discard so the discard handles the common case and the epoch
    // covers only the genuine post-fence stragglers.
    beginReloadFence()

    // Re-baseline EVERY lane's dedupe cache BEFORE the reload: openServerProject
    // re-emits the editor + store subscriptions synchronously (its set() and
    // loadApp), and with the OLD caches those emissions would be treated as
    // edits and spuriously re-queue the just-loaded content. With the caches
    // cleared the emissions re-baseline instead. The restorer puts them back if
    // the reload fails (the project never reloaded → the pre-reload
    // change-detection state must stand, or the next edit is swallowed).
    const restoreDedupe = resetAllDedupeSnapshots()

    // Reload. openServerProject swallows load failures (sets projectLoadError,
    // leaves currentProjectId unchanged) — so do NOT trust the resolved
    // promise; detect success by inspecting the post-load state.
    await storeApi.getState().openServerProject(projectId)

    const after = storeApi.getState()
    const reloadSucceeded = after.currentProjectId === projectId && !after.projectLoadError
    if (!reloadSucceeded) {
      // L2-COR-03 — the reload did NOT happen. Restore change-detection (so the
      // user's next edit is still detected), keep the conflict armed (the
      // dismissal guard must stay up over the diverged, unsaved local state),
      // and tell the user why nothing changed.
      restoreDedupe()
      toast.error('Reload failed — your changes are kept. Check your connection and try Reload again.')
      return false
    }

    // SUCCESS — the reloaded state is adopted. The dedupe caches stay cleared
    // (re-baselined from the reloaded emissions), so a byte-identical re-apply
    // of the conflicted edit is queued, not suppressed (RUX-1/PI-L2-05). Now
    // drop the conflict record.
    storeApi.getState().clearSaveConflict()
    return true
  } finally {
    resolveConflictInProgress = false
    storeApi.setState({ isResolvingConflict: false } as never)
  }
}

// Change detection via full serialization — length alone cannot detect
// same-length edits or sibling reorders (same total string length), which
// silently skipped auto-save. Same failure class as the flowSteps
// Phase 378 Plan 02 fix below.
function buildEditorSnapshot(app: unknown, blocks: Record<string, unknown>) {
  return [
    `app:${JSON.stringify(app)}`,
    ...Object.entries(blocks).map(([k, v]) => `${k}:${JSON.stringify(v)}`),
  ]
    .join(',')
}

/**
 * Initialize auto-save subscriptions. Call once at app startup.
 * Only queues saves for server-persisted projects.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function initAutoSaveBridge(store: any) {
  if (initialized) {
    // Repeat init: keep the existing subscriptions (their callbacks resolve
    // the store via getStudioStore() at FIRE time) but adopt the new store
    // reference. Production calls this exactly once (studio-store module
    // init); the vitest integration suite, however, injects a fake store
    // from beforeAll — and depending on worker module-graph scheduling,
    // studio-store can get evaluated in the same registry FIRST, win this
    // init, and silently turn the test's injection into a no-op (every
    // gate then reads the real store and no save ever queues).
    _studioStore = store
    registerAutoSaveProjectIdSource(() => store.getState().currentProjectId ?? null)
    return
  }
  _studioStore = store

  // Pending saves capture the project they belong to at QUEUE time, so a
  // project switch inside the debounce window can't reroute or drop them.
  registerAutoSaveProjectIdSource(() => store.getState().currentProjectId ?? null)

  // --- Page-dismissal protocol (F27) ---
  //
  // Tab close was the ONLY exit path that never flushed: both debounced
  // lanes (queueSave field PATCHes and the full editor persist) were
  // silently dropped, and a transient save failure + close = guaranteed
  // loss (the failure re-queue waits for the next edit/flush by design).
  //
  // visibilitychange(hidden) is the reliable flush point: the document is
  // still alive, so the `await requireUserJwt()` that precedes every
  // workerRequest fetch can complete (in-repo precedent: repo-telemetry's
  // pagehide flush), and `keepalive` lets BOTH lanes' writes outlive the
  // unload if the user proceeds — lane 1 (flushSaves) and lane 2's PUT +
  // manifest PATCH (flushEditorFullPersist), both now keepalive-threaded
  // (L2-COR-05/CS-4/RUX-3).
  //
  // beforeunload is the user-consent gate AND a lane coverage backstop: on a
  // DIRECT close (tab never hidden first) the event order is beforeunload →
  // dialog → pagehide → visibilitychange(hidden), so the visibilitychange
  // flush would only run during teardown. So beforeunload kicks BOTH lanes
  // with keepalive itself — lane 2 only when it holds pending work, to avoid
  // the opportunistic-capture whole-replace PUT (L2-COR-01 P0).
  //
  // HONEST RESIDUAL: keepalive bodies share a ~64KB in-flight quota; a
  // full-project lane-2 PUT can exceed it → the fetch rejects and the
  // preventDefault dialog is the only protection (same residual class as
  // lane-1's oversize re-queue).
  //
  // Registered ONLY on the first init — the repeat-init early-return above
  // guarantees a single registration (no double flushes per event). The
  // listeners are also torn down on Vite HMR dispose (CS-9) so a module
  // re-evaluation cannot leave stale-closure handlers double-firing. The
  // thumbnail lane keeps its own beforeunload flush in auto-save.ts (it is
  // an auth-less sendBeacon transport, deliberately untouched).
  if (typeof window !== 'undefined') {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return
      void flushSaves({ keepalive: true }).catch(() => {})
      // Lane 2 flushes ONLY when it actually holds pending work (debounce
      // armed / captured snapshot / in flight). hasPendingEditorPersist()
      // is exactly the "the user has lane-2 work" predicate — calling
      // flushEditorFullPersist unconditionally here let its opportunistic
      // capture (reserved for user-intent publish flushes; see the comment
      // at the capture site) turn a zero-edit tab switch in the default
      // preview-rail session into an unrequested whole-replace PUT of
      // load-time content (L2-COR-01 P0).
      if (hasPendingEditorPersist()) {
        void flushEditorFullPersist({ keepalive: true }).catch(() => {})
      }
    }
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldBlockDismissal()) return
      event.preventDefault()
      // Legacy engines need returnValue set for the native dialog.
      event.returnValue = ''
      void flushSaves({ keepalive: true }).catch(() => {})
      // L2-COR-05/CS-4 — also kick lane 2 with keepalive here: on a direct
      // close the visibilitychange flush would only fire during teardown,
      // and lane-2's PUT is no-keepalive-by-default → silently lost. Gated on
      // pending work for the same L2-COR-01 reason as the visibility path.
      if (hasPendingEditorPersist()) {
        void flushEditorFullPersist({ keepalive: true }).catch(() => {})
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('beforeunload', onBeforeUnload)

    // CS-9 — Vite HMR teardown. The `initialized` flag is module-instance
    // scoped: a second module evaluation (HMR re-eval in dev / a dual test
    // module graph) would re-register these listeners while the OLD
    // instance's handlers stay attached, closing over stale module state
    // (pendingChanges / editorFullPersistTimer / _studioStore) — double
    // flushes and a phantom beforeunload block off dead state. Dispose
    // removes them so a re-eval starts clean. Dev-only (production evaluates
    // the module once); guarded so the production bundle never touches it.
    if (import.meta.hot) {
      import.meta.hot.dispose(() => {
        document.removeEventListener('visibilitychange', onVisibilityChange)
        window.removeEventListener('beforeunload', onBeforeUnload)
        initialized = false
      })
    }
  }

  // --- EditorStore → auto-save (page/block changes) ---
  useEditorStore.subscribe((state) => {
    const { blocks, app } = state
    if (!blocks || !app) return

    const store = getStudioStore().getState()
    if (editorAutoSaveSuppressionDepth > 0) {
      lastEditorProjectId = store.currentProjectId ?? ''
      lastBlocksSnapshot = buildEditorSnapshot(
        app,
        blocks as Record<string, unknown>,
      )
      return
    }
    if (store.projectSource !== 'server' || !store.currentProjectId) return

    // Sync editor changes in classic editor mode and IDE v2 Page surface.
    // The rail item is the authoritative IDE v2 signal; viewMode may stay
    // "preview" while the Page surface is mounted.
    if (store.viewMode !== 'editor' && store.activeRailItem !== 'editor') return

    // Change detection — include app manifest/pages and block keys + sizes.
    const snapshot = buildEditorSnapshot(app, blocks as Record<string, unknown>)

    const editorProjectChanged = store.currentProjectId !== lastEditorProjectId
    if (editorProjectChanged) {
      lastEditorProjectId = store.currentProjectId
      lastBlocksSnapshot = snapshot
      return
    }

    if (shouldSkipEditorPersistSnapshot({
      projectChanged: editorProjectChanged,
      snapshot,
      lastSnapshot: lastBlocksSnapshot,
    })) return
    lastBlocksSnapshot = snapshot

    store.markDirty()

    const { blockIdMap } = store
    const hasUnpersistedBlock = Object.keys(blocks).some((blockName) => !blockIdMap[blockName])
    if (hasUnpersistedBlock) {
      scheduleFullEditorPersist({
        projectId: store.currentProjectId,
        app,
        blocks: blocks as Record<string, unknown>,
        flows: state.flows as Record<string, unknown>,
      })
      mirrorEditorBlocksIntoProject(blocks as Record<string, unknown>, app)
      return
    }

    // Queue block saves — shared canonical ui payload builder (PI-4: the F3
    // flow-rewrite path writes the same `block:<id>:ui` LWW key and must
    // queue the identical shape).
    for (const [blockName, blockDef] of Object.entries(blocks)) {
      const dbId = blockIdMap[blockName]
      if (!dbId) continue

      queueSave({
        type: 'block',
        blockId: dbId,
        field: 'ui',
        data: buildUiFieldPayload(blockDef as unknown as Record<string, unknown>),
      })
    }

    // Save manifest. Readiness locations are server-owned even on the ordinary
    // queued page-edit path; project them while preserving creator siblings.
    const manifest = withServerDatasourceReadinessManifestAuthority(
      app,
      store.projectMeta,
    )
    queueSave({
      type: 'project',
      field: 'manifest',
      data: manifest,
    })

    // Sync back to currentProject for preview (field-scoped — see the
    // ownership rule on mergeEditorBlocksPreservingStudioFields).
    mirrorEditorBlocksIntoProject(blocks as Record<string, unknown>, manifest)
  })

  // --- FlowEditorStore flowSteps → auto-save (step CRUD changes) ---
  //
  // This is the SINGLE flow persistence path. The former canvas-node
  // subscription was removed (F50): the canvas projection round-trip
  // (flowToCanvas → canvasToFlow) is lossy — loop/switch/tryCatch/parallel
  // bodies are dropped — and it fired on mere flow selection, rewriting every
  // persisted flow with a gutted reconstruction into the first block. Until
  // the canvas round-trip is lossless, the canvas is display-only and must
  // never feed queueSave or the currentProject.flows preview mirror.
  let lastFlowStepsSnapshot = ''
  // PI-L2-05 / RUX-1 — a conflict reload must clear this closure-scoped cache,
  // else a byte-identical re-apply of the conflicted flow edit after Reload is
  // dedupe-suppressed at the `snapshot === lastFlowStepsSnapshot` gate below
  // (never queued, never dirty → silent loss). resolveSaveConflictByReload
  // invokes this on a successful reload.
  registerDedupeSnapshotReset(() => {
    const prev = lastFlowStepsSnapshot
    lastFlowStepsSnapshot = ''
    return () => { lastFlowStepsSnapshot = prev }
  })
  useFlowEditorStore.subscribe((state) => {
    const { flowSteps, flowStepsDirty, selectedFlowId, flows } = state
    if (!selectedFlowId || flows.length === 0) return

    // Only user-initiated step mutations persist. Hydration, flow selection,
    // and panel re-seeding leave flowStepsDirty unset — without this gate,
    // selecting a flow queued the PREVIOUS flow's (or an empty) step array
    // under the new flow's name (F50 verifier addendum).
    if (!flowStepsDirty) return

    const store = getStudioStore().getState()
    if (store.projectSource !== 'server' || !store.currentProjectId) return

    // Change detection via full serialization — length+content-length alone
    // cannot detect reorder (same length, same total string length). This
    // caused reorder edits to silently skip auto-save. Phase 378 Plan 02 fix.
    // The snapshot is recorded only AFTER queueSave succeeds: recording it
    // before the routing bail-outs below would dedupe-suppress a retry of the
    // identical state, making a routing-failed edit permanently unsaveable.
    const snapshot = `steps:${selectedFlowId}:${flowSteps.length}:${JSON.stringify(flowSteps)}`
    if (snapshot === lastFlowStepsSnapshot) return

    const flowMeta = flows.find((f: { id: string; name: string }) => f.id === selectedFlowId)
    if (!flowMeta) return

    const project = store.currentProject
    if (!project?.blocks) return

    // Route the save to the block that owns the flow (declaration first,
    // event binding second — see flow-ownership.ts). A brand-new flow that
    // is neither declared nor event-bound yet falls back to the first block:
    // the LoadedApp schema requires every flow to live on some block, and
    // the first block matches toLoadedApp's first-declarer reload merge.
    const blocks = project.blocks as Record<string, OwnedBlock>
    const resolved = resolveFlowOwner(blocks, flowMeta.name)
    const ownerBlockName = resolved?.blockName ?? Object.keys(blocks)[0]
    if (!ownerBlockName) return
    const flowKey = resolved?.flowKey ?? flowMeta.name

    const blockId = store.blockIdMap[ownerBlockName]
    if (!blockId) {
      // Unroutable edit (e.g. chat-generated project whose blocks are still
      // in-memory only): keep the dirty indicator honest instead of silently
      // claiming saved. The un-recorded snapshot lets the next subscription
      // fire retry once blockIdMap is populated.
      console.warn(`[auto-save-bridge] No block id for flow owner "${ownerBlockName}" — step edit not yet persistable`)
      store.markDirty()
      return
    }

    store.markDirty()

    // Build updated flow data: merge flowSteps into the owner block's flow
    // JSON under its declared key. New Behavior flows are often selected as
    // project-level flows, while the persisted LoadedApp format reconstructs
    // project.flows from block.flow. Keep both mirrors in sync so reload,
    // Preview, and Code read the same step array.
    const currentFlow = (project.blocks[ownerBlockName] as { flow?: Record<string, unknown> })?.flow ?? {}
    const { payload: updatedFlow, flow: updatedSingleFlow } = mergeFlowStepsForSave({
      currentFlow,
      flowName: flowKey,
      trigger: (flowMeta as { trigger?: unknown }).trigger,
      flowSteps,
    })

    queueSave({
      type: 'block',
      blockId,
      field: 'flow',
      data: updatedFlow,
    })
    lastFlowStepsSnapshot = snapshot

    // F6: deep-validate the EXACT payload just queued and publish a
    // flow-scoped 'actionflow-edit' verdict. Fire-and-forget — validation
    // must NEVER gate, delay, or fail the save above (D-406's fail-closed
    // contract governs generation/flywheel admission, not user saves).
    try {
      void validateFlowEditAndPublish({
        ownerBlock: project.blocks[ownerBlockName],
        ownerBlockName,
        flowKey,
        updatedFlow,
      }).catch(() => {
        // The helper is contractually non-throwing; absorb anyway so a
        // misbehaving (e.g. mocked) implementation can't surface an
        // unhandled rejection out of the persistence path.
      })
    } catch {
      // Same guarantee for synchronous throws.
    }

    getStudioStore().setState((s: any) => {
      if (!s.currentProject) return
      s.currentProject.flows = {
        ...(s.currentProject.flows ?? {}),
        [flowKey]: updatedSingleFlow,
      }
      if (s.currentProject.blocks?.[ownerBlockName]) {
        s.currentProject.blocks[ownerBlockName].flow = updatedFlow
      }
    })
  })

  // --- StorySlice -> localStorage persistence (STUDIO-UX-02) ---
  // Story changes (especially interaction scenarios) are persisted to localStorage
  // so they survive page reload. Works for all project types (template and server).
  let lastStorySnapshot = ''

  _studioStore.subscribe((state: any) => {
    const story = state.story
    if (!story) return
    const projectId = state.currentProjectId
    if (!projectId) return

    // Change detection
    const snapshot = JSON.stringify(story)
    if (snapshot === lastStorySnapshot) return
    lastStorySnapshot = snapshot

    // Persist to localStorage
    try {
      localStorage.setItem(`cogo-story-${projectId}`, snapshot)
    } catch { /* ignore quota errors */ }
  })

  // --- StudioStore variables → auto-save (DATA-02) ---
  let lastVariablesSnapshot = ''
  let lastVariablesProjectId = ''
  // PI-L2-05 — reset BOTH the dedupe snapshot and the projectId marker. After a
  // same-project reload, currentProjectId is unchanged, so the projectChanged
  // re-baseline branch would NOT fire; clearing the marker forces it to, and
  // clearing the snapshot lets an identical re-applied variables edit re-queue
  // instead of being suppressed.
  registerDedupeSnapshotReset(() => {
    const prevSnap = lastVariablesSnapshot
    const prevPid = lastVariablesProjectId
    lastVariablesSnapshot = ''
    lastVariablesProjectId = ''
    return () => { lastVariablesSnapshot = prevSnap; lastVariablesProjectId = prevPid }
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _studioStore.subscribe((state: any) => {
    const project = state.currentProject
    if (!project?.blocks) return

    if (state.projectSource !== 'server' || !state.currentProjectId) return

    // Build snapshot across all blocks' variables
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const snapshot = Object.entries(project.blocks as Record<string, any>)
      .map(([name, block]) => `${name}:${JSON.stringify(getPersistableVariables(block as Record<string, unknown>) ?? {})}`)
      .join(',')

    const variablesProjectChanged = state.currentProjectId !== lastVariablesProjectId
    if (variablesProjectChanged) {
      lastVariablesProjectId = state.currentProjectId
    }
    if (shouldSkipPersistSnapshot({
      projectChanged: variablesProjectChanged,
      isDirty: state.isDirty,
      saveError: state.saveError,
      snapshot,
      lastSnapshot: lastVariablesSnapshot,
    })) {
      lastVariablesSnapshot = snapshot
      return
    }
    lastVariablesSnapshot = snapshot

    state.markDirty?.()

    // Queue save for each block that has variables
    const { blockIdMap } = state
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const [blockName, block] of Object.entries(project.blocks as Record<string, any>)) {
      const dbId = blockIdMap[blockName]
      const variables = getPersistableVariables(block as Record<string, unknown>)
      if (!dbId || !variables) continue

      queueSave({
        type: 'block',
        blockId: dbId,
        field: 'variables',
        data: variables,
      })
    }
  })

  // --- DesignSystem → auto-save (Phase 403-01: DS-01/DS-02) ---
  // Persist 6-axis design system state to project settings column.
  // Also updates currentProject.designSystem (flat token map for validator compat).
  let lastDesignSystemSnapshot = ''
  let lastDesignSystemProjectId = ''
  // PI-L2-05 — reset both (see variables lane note): the reload re-seeds the
  // design system inside openServerProject's set(), and the projectId marker
  // would otherwise block re-baselining on a same-project reload.
  registerDedupeSnapshotReset(() => {
    const prevSnap = lastDesignSystemSnapshot
    const prevPid = lastDesignSystemProjectId
    lastDesignSystemSnapshot = ''
    lastDesignSystemProjectId = ''
    return () => { lastDesignSystemSnapshot = prevSnap; lastDesignSystemProjectId = prevPid }
  })

  _studioStore.subscribe((state: any) => {
    const ds = state.designSystem
    if (!ds) return

    if (state.projectSource !== 'server' || !state.currentProjectId) return

    const snapshot = JSON.stringify(ds)
    // RUX-2 — re-baseline on project open/switch instead of queueing: the
    // first emission for a newly opened project carries the JUST-LOADED
    // design system (openServerProject restores designSystemState inside
    // the same set() that flips currentProjectId), not a user edit.
    // Pre-F27 this echo was merely a spurious settings PATCH; with the
    // dismissal guard wired to hasPendingChanges() it armed the native
    // "Leave site?" dialog for the debounce+flush window on EVERY project
    // open with zero user edits. The variables/datasource lanes already
    // re-baseline via their projectChanged gates — this mirrors them.
    if (state.currentProjectId !== lastDesignSystemProjectId) {
      lastDesignSystemProjectId = state.currentProjectId
      lastDesignSystemSnapshot = snapshot
      return
    }
    if (snapshot === lastDesignSystemSnapshot) return
    lastDesignSystemSnapshot = snapshot

    state.markDirty?.()

    // Serialize and save to project settings column.
    // F2 — the queued object is an OVERLAY, not the full settings document:
    // auto-save dispatch merges it onto a fresh read of the row's current
    // settings (saveProjectMetaMergingSettings) before the PATCH, so
    // sibling keys this lane does not own (settings.generation, receipt-
    // bound application settings, ontology selection) survive a
    // design-system edit. Queue only the keys this lane owns here.
    const serialized = serializeDesignSystem(ds)
    const flatTokens = toDesignSystemJson(ds)

    queueSave({
      type: 'project',
      field: 'settings',
      data: withStudioProjectPlanSettings(
        { designSystemState: serialized.axes, designSystemTokens: flatTokens },
        state.projectPlan ?? state.projectMeta?.studioProjectPlan,
      ),
    })
    const nativeTokens = computeThemeTokens(ds)
    queueSave({
      type: 'project',
      field: 'tokens',
      data: nativeTokens,
    })

    // `designSystem` is retained for the existing validator compatibility
    // surface. The renderer consumes the native ThemeTokens object instead.
    getStudioStore().setState((s: any) => {
      if (s.currentProject) {
        s.currentProject.designSystem = flatTokens
        s.currentProject.tokens = nativeTokens
      }
    })
  })

  // --- StudioStore datasource → auto-save (DATA-05) ---
  let lastDatasourceSnapshot = ''
  let lastDatasourceProjectId = ''
  // PI-L2-05 — reset both (see variables lane note).
  registerDedupeSnapshotReset(() => {
    const prevSnap = lastDatasourceSnapshot
    const prevPid = lastDatasourceProjectId
    lastDatasourceSnapshot = ''
    lastDatasourceProjectId = ''
    return () => { lastDatasourceSnapshot = prevSnap; lastDatasourceProjectId = prevPid }
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _studioStore.subscribe((state: any) => {
    const project = state.currentProject
    if (!project?.blocks) return

    if (state.projectSource !== 'server' || !state.currentProjectId) return

    // Build snapshot across all blocks' datasource
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const snapshot = Object.entries(project.blocks as Record<string, any>)
      .map(([name, block]) => `${name}:${JSON.stringify(getPersistableDatasource(block as Record<string, unknown>) ?? {})}`)
      .join(',')

    const datasourceProjectChanged = state.currentProjectId !== lastDatasourceProjectId
    if (datasourceProjectChanged) {
      lastDatasourceProjectId = state.currentProjectId
    }
    if (shouldSkipPersistSnapshot({
      projectChanged: datasourceProjectChanged,
      isDirty: state.isDirty,
      saveError: state.saveError,
      snapshot,
      lastSnapshot: lastDatasourceSnapshot,
    })) {
      lastDatasourceSnapshot = snapshot
      return
    }
    lastDatasourceSnapshot = snapshot

    state.markDirty?.()

    // Queue save for each block that has datasource
    const { blockIdMap } = state
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    for (const [blockName, block] of Object.entries(project.blocks as Record<string, any>)) {
      const dbId = blockIdMap[blockName]
      const datasource = getPersistableDatasource(block as Record<string, unknown>)
      if (!dbId || !datasource) continue

      queueSave({
        type: 'block',
        blockId: dbId,
        field: 'datasource',
        data: datasource,
      })
    }
  })

  // Mark initialization complete only after every persistence lane has
  // registered. During the studio-store <-> auto-save-bridge ESM cycle, the
  // deferred retry path can enter this function while dependent store modules
  // are still settling; if a partial init throws, a premature true here would
  // make every retry adopt the store but skip the missing subscriptions.
  initialized = true
}

// syncFlowsToProject (canvas → currentProject.flows force-sync) was removed
// with the canvas persistence path: it replaced the preview mirror with the
// lossy canvasToFlow projection. The flowSteps subscription above keeps
// currentProject.flows in sync on every user edit, so no pre-preview sync
// is needed.

/** Cmd+S handler — flush all pending saves immediately */
export async function handleManualSave(): Promise<boolean> {
  const store = getStudioStore().getState()
  if (store.projectSource !== 'server' || !store.currentProjectId) return false

  try {
    await flushSaves()
    return true
  } catch (err) {
    console.error('[auto-save-bridge] Manual save failed:', err)
    return false
  }
}
